/**
 * Platform Admin Resource & Task Control V2 — small shared
 * formatting/UI primitives used by both `OverviewPage.tsx` (Resource
 * cards + Worker table) and `TaskControlSection.tsx` (the new Task
 * Control table). Extracted rather than duplicated a second time so the
 * two surfaces never silently drift (e.g. two different date formats).
 *
 * Platform Admin Density & Worker Liveness P0 — `SummaryCard` was
 * rebuilt more compact per spec §4 (~105-125px height, 14-18px padding,
 * smaller icon/title/hint) and a new `WorkerStatusBadge` was added per
 * spec §14/§15 (single canonical `display_status` -> Offline/Disabled/
 * Draining/Busy/Online badge, reused by every surface that renders a
 * Worker's status so none of them can re-invent their own color/label
 * mapping).
 */
import { Skeleton, Tooltip } from 'antd'
import { platformColors } from './theme'

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '暂不可用'
  if (bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let value = bytes
  let unitIndex = 0
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024
    unitIndex += 1
  }
  return `${value.toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`
}

export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const diffSeconds = Math.max(0, Math.round((Date.now() - then) / 1000))
  if (diffSeconds < 60) return `${diffSeconds} 秒前`
  const diffMinutes = Math.round(diffSeconds / 60)
  if (diffMinutes < 60) return `${diffMinutes} 分钟前`
  const diffHours = Math.round(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours} 小时前`
  const diffDays = Math.round(diffHours / 24)
  return `${diffDays} 天前`
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('zh-CN', { hour12: false })
}

const STATUS_TAG_COLOR: Record<string, { bg: string; color: string }> = {
  queued: { bg: platformColors.warningBg, color: platformColors.warning },
  running: { bg: platformColors.infoBg, color: platformColors.info },
  waiting_for_review: { bg: platformColors.warningBg, color: platformColors.warning },
  succeeded: { bg: platformColors.successBg, color: platformColors.success },
  failed: { bg: platformColors.dangerBg, color: platformColors.danger },
  cancelled: { bg: 'var(--pf-neutral-bg)', color: platformColors.secondaryText },
  interrupted: { bg: platformColors.warningBg, color: platformColors.warning },
}

export function StatusBadge({ status, label }: { status: string; label: string }) {
  const tone = STATUS_TAG_COLOR[status] ?? { bg: 'var(--pf-neutral-bg)', color: platformColors.secondaryText }
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 12,
        fontWeight: 500,
        padding: '2px 8px',
        borderRadius: 4,
        background: tone.bg,
        color: tone.color,
      }}
    >
      {label}
    </span>
  )
}

/** Platform Admin Density & Worker Liveness P0 spec §4 — compact metric
 * card: height ~108px (padding 14 + icon 32 + value line + one hint
 * line), padding 14px, title 12px, value 22px, hint 11px, icon 32px,
 * radius 9px. Deliberately still comfortably readable (spec §2: "不要
 * 变成拥挤难读") — only the dead vertical whitespace around each of
 * those elements was removed, no font shrunk below spec's own floor. */
export function SummaryCard({
  title,
  value,
  hint,
  iconBg,
  icon,
  loading,
  technicalNote,
  onClick,
}: {
  title: string
  value: React.ReactNode
  hint?: React.ReactNode
  iconBg: string
  icon: React.ReactNode
  loading?: boolean
  /** Spec §25 — a technical/implementation-detail string (e.g. a raw SQL
   * source or "no Cloudflare Analytics API credentials configured...")
   * that must never occupy first-screen space; rendered only inside a
   * small (i) tooltip next to the title, never inline. */
  technicalNote?: string | null
  /** External Services summary card (P0-B spec §21) — clicking it
   * navigates into "外部服务"; every other existing card leaves this
   * unset and stays non-interactive. */
  onClick?: () => void
}) {
  return (
    <div
      onClick={onClick}
      style={{
        background: platformColors.surface,
        border: `1px solid ${platformColors.border}`,
        borderRadius: 9,
        padding: 14,
        minHeight: 100,
        boxShadow: 'var(--pf-shadow-card)',
        flex: '1 1 190px',
        minWidth: 190,
        cursor: onClick ? 'pointer' : undefined,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, lineHeight: '16px', fontWeight: 500, color: platformColors.secondaryText }}>
          <span>{title}</span>
          {technicalNote && (
            <Tooltip title={technicalNote}>
              <span style={{ cursor: 'help', color: platformColors.mutedText, fontSize: 11 }}>ⓘ</span>
            </Tooltip>
          )}
        </div>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 7,
            background: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
      </div>
      <div style={{ marginTop: 4 }}>
        {loading ? (
          <Skeleton.Input active size="small" style={{ width: 68, height: 24 }} />
        ) : (
          <div style={{ fontSize: 22, lineHeight: '26px', fontWeight: 600, color: platformColors.primaryText }}>{value}</div>
        )}
      </div>
      {hint && (
        <div
          style={{
            fontSize: 11,
            lineHeight: '15px',
            color: platformColors.mutedText,
            marginTop: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {hint}
        </div>
      )}
    </div>
  )
}

export function SectionCard({
  title,
  extra,
  children,
  allowOverflow,
}: {
  title: React.ReactNode
  extra?: React.ReactNode
  children: React.ReactNode
  /** VISTOERA_PLATFORM_WORKER_STICKY_HEADER_CAPABILITY_MATRIX_VISIBILITY_
   * AND_PROVIDER_RUNTIME_STATE_UNIFICATION_P0_V1 (item 十七) — forensic
   * root cause of "sticky header disappears on scroll": `overflow:
   * hidden` on THIS wrapper (kept below for the default case, purely to
   * clip the title bar to the rounded corners) becomes the nearest
   * ancestor with a non-`visible` overflow, which per the CSS spec is
   * what `position: sticky` computes its containing block against. That
   * ancestor never itself scrolls (the real scrolling happens further up
   * the tree, e.g. `.pf-content`), so from the sticky header's
   * perspective its scroll position never changes and it can never
   * "stick" — it just scrolls away with the rest of the table. Pass
   * `allowOverflow` for any `SectionCard` that wraps a `sticky` antd
   * `Table` (default stays `overflow: hidden` everywhere else — this is
   * a purely additive, opt-in fix, never a behavior change for the other
   * 30+ existing `SectionCard` call sites). */
  allowOverflow?: boolean
}) {
  return (
    <div
      style={{
        background: platformColors.surface,
        border: `1px solid ${platformColors.border}`,
        borderRadius: 10,
        marginTop: 8,
        overflow: allowOverflow ? 'visible' : 'hidden',
      }}
    >
      <div
        style={{
          padding: '7px 14px',
          fontSize: 14,
          fontWeight: 600,
          color: platformColors.primaryText,
          borderBottom: `1px solid ${platformColors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <span>{title}</span>
        {extra}
      </div>
      <div style={{ padding: 6 }}>{children}</div>
    </div>
  )
}

/** Platform Admin Density & Worker Liveness P0 spec §14/§15 — the ONE
 * place every surface (Overview Worker table, Workers page, Task
 * Control worker column) turns `display_status` into a badge. Legacy
 * callers that only have a plain `online: boolean` (no `display_status`
 * yet, e.g. an older cached response) still get a correct two-state
 * Online/Offline badge via the `online` fallback prop — never a hard
 * crash on a missing field. */
const WORKER_STATUS_TONE: Record<string, { bg: string; color: string; label: string }> = {
  online: { bg: platformColors.successBg, color: platformColors.success, label: '在线' },
  busy: { bg: platformColors.infoBg, color: platformColors.info, label: '忙碌' },
  offline: { bg: platformColors.dangerBg, color: platformColors.danger, label: '离线' },
  draining: { bg: platformColors.warningBg, color: platformColors.warning, label: '排空中' },
  disabled: { bg: 'var(--pf-neutral-bg)', color: platformColors.secondaryText, label: '已禁用' },
}

export function WorkerStatusBadge({ displayStatus, online }: { displayStatus?: string | null; online?: boolean }) {
  const key = displayStatus ?? (online ? 'online' : 'offline')
  const tone = WORKER_STATUS_TONE[key] ?? WORKER_STATUS_TONE.offline
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 600,
        padding: '1px 7px',
        borderRadius: 4,
        background: tone.bg,
        color: tone.color,
        whiteSpace: 'nowrap',
      }}
    >
      {tone.label}
    </span>
  )
}

/** Spec §24 — "10s前" style freshness, short enough for a card hint
 * line; falls back to the plain relative time for anything older than
 * a minute (matches `formatRelativeTime`'s own granularity). */
export function formatFreshness(iso: string | null | undefined): string {
  return formatRelativeTime(iso)
}

/** VISTOERA_AI_CHAT_CONTROL_PLANE_P0 spec §9 — one componentized badge per
 * AI Chat provider (never a per-page repeated provider->color/label
 * switch statement). Colors reuse the existing semantic status tokens
 * (never a new hardcoded hex) purely to keep the three providers visually
 * distinct at a glance; they carry no status meaning of their own. */
const PROVIDER_TONE: Record<string, { bg: string; color: string }> = {
  chatgpt: { bg: platformColors.successBg, color: platformColors.success },
  gemini: { bg: platformColors.infoBg, color: platformColors.info },
  grok: { bg: platformColors.primarySoft, color: platformColors.primary },
  qwen: { bg: platformColors.infoBg, color: platformColors.info },
  deepseek: { bg: platformColors.primarySoft, color: platformColors.primary },
}

export function ProviderBadge({ provider, label }: { provider: string; label: string }) {
  const tone = PROVIDER_TONE[provider] ?? { bg: 'var(--pf-neutral-bg)', color: platformColors.secondaryText }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 12,
        fontWeight: 500,
        padding: '2px 8px',
        borderRadius: 4,
        background: tone.bg,
        color: tone.color,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  )
}

/** Progress bar for resource cards (spec §六: "45% 已使用，并提供进度条"). */
export function UsageBar({ used, total }: { used: number | null; total: number | null }) {
  if (used === null || total === null || total <= 0) return null
  const pct = Math.min(100, Math.max(0, (used / total) * 100))
  const color = pct >= 90 ? platformColors.danger : pct >= 70 ? platformColors.warning : platformColors.primary
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ height: 6, borderRadius: 3, background: 'var(--pf-neutral-bg)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.3s ease' }} />
      </div>
      <div style={{ fontSize: 11, color: platformColors.mutedText, marginTop: 3 }}>{pct.toFixed(0)}% 已使用</div>
    </div>
  )
}
